import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";
import { editReport, createReport, deleteReport } from "../../services/api/reports.api";
import { useHistory } from "react-router-dom";
import { FormikTextInputField } from "../../components/Form/FormikTextInput";
import { useSnackbar } from "notistack";
import { Formik } from "formik";
import { showSnack } from "../../utils/component";
import { ReportEditRequestValidationSchema, ReportEditRequest } from "../../models/Reports";
import { tryGetProp } from "../../utils";

interface ReportEditProps {
	// product id
	productId: string;
	//Report
	report?: ReportEditRequest;
}

const useStyles = makeStyles((theme) => ({
	wrapper: {
		marginBottom: theme.spacing(4),
		maxWidth: 1024,
	},
	title: {
		marginBottom: theme.spacing(4),
	},
	url: {
		display: "inline",
		maxWidth: 700,
	},
	details: {
		fontSize: theme.typography.body1.fontSize,
		display: "inline",
		maxWidth: 700,
	},
	saveButton: {
		display: "inline",
		float: "right",
	},
	iFrame: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
}));

export const ReportEditRequestInitialValue: ReportEditRequest = {
	description: "",
	name: "",
	productId: "",
	url: "",
	reportId: "",
};

/**
 * Page to display subscription details and allow configuration editing
 * @param props
 */
export const ReportEdit = (props: ReportEditProps) => {
	const classes = useStyles();
	const history = useHistory();
	const { enqueueSnackbar } = useSnackbar();
	const [isFormSubmitting, setIsFormSubmitting] = useState(false);

	const initialValues: ReportEditRequest = {
		description: tryGetProp(ReportEditRequestInitialValue.description!, () => props.report!.description),
		name: tryGetProp(ReportEditRequestInitialValue.name!, () => props.report!.name),
		url: tryGetProp(ReportEditRequestInitialValue.url!, () => props.report!.url),
		reportId: tryGetProp(ReportEditRequestInitialValue.reportId!, () => props.report!.reportId),
		productId: props.productId,
	};
	const isEditMode = props.report != null;

	const submitForm = async (values, { resetForm }) => {
		try {
			setIsFormSubmitting(true);
			let snackBarMessage = "Changes saved successfully";
			if (isEditMode) {
				await editReport(props.productId, values)();
			} else {
				await createReport(props.productId, values)();
				snackBarMessage = "Report successfully saved.";
				resetForm();
			}
			showSnack(enqueueSnackbar, snackBarMessage);
		} catch (error: any) {
			console.log({ error });
			if (error.request.status == 409) {
				showSnack(enqueueSnackbar, "Report Url or Name is not unique", "error");
			} else {
				showSnack(enqueueSnackbar, "There was an error while saving changes", "error");
			}
		}

		setIsFormSubmitting(false);
	};

	const handleDelete = () => {
		deleteReport(props.productId, props.report!.reportId)()
			.then(() => {
				history.push(`/Products/${props.productId}/Reports`);
			})
			.catch((error) => {
				console.log({ error });
				showSnack(enqueueSnackbar, "There was an error while deleting the report", "error");
			});
	};

	return (
		<div className={classes.wrapper} data-testid="reports-edit">
			<div>
				<Formik
					initialValues={initialValues}
					onSubmit={submitForm}
					validationSchema={ReportEditRequestValidationSchema}
				>
					{({ handleSubmit, values, errors }) => {
						return (
							<div style={{ marginTop: 30 }}>
								<div>
									<FormikTextInputField name="name" label="Name" optimized={true} />
								</div>
								<div className={classes.url}>
									<FormikTextInputField
										name="url"
										label="URL"
										optimized={true}
										formControlWidth="100%"
										inputWidth="100%"
									/>
								</div>
								{isEditMode && (
									<div className={classes.iFrame}>
										<iframe width={"100%"} height={600} src={errors.url ? "" : values.url}></iframe>
									</div>
								)}
								<div className={classes.details}>
									<FormikTextInputField
										name="description"
										label="Description"
										optimized={true}
										isTextArea={true}
										formControlWidth="100%"
										inputWidth="100%"
									/>
								</div>

								<div style={{ marginTop: 20 }}>
									<Button
										data-testid="edit-report-save-btn"
										variant="contained"
										disabled={isFormSubmitting}
										color="primary"
										style={{ marginRight: 12 }}
										onClick={() => handleSubmit()}
									>
										{isEditMode ? "Save report" : "Add report"}
									</Button>
									{isEditMode && (
										<Button
											data-testid="edit-report-delete-btn"
											variant="outlined"
											disabled={isFormSubmitting}
											color="secondary"
											onClick={() => {
												if (confirm("Are you sure you want to delete this report?")) {
													handleDelete();
												}
											}}
										>
											Delete report
										</Button>
									)}
								</div>
							</div>
						);
					}}
				</Formik>
			</div>
		</div>
	);
};
